import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The Hide and Seek Teams coin: Rotworms on one face, Frogdogs on the other.
 * Given a flip, it spins in 3D and lands on the winning face. The spin is a CSS
 * animation (@swtorbit/ui's .hnst-coin) as long as the server's
 * HNST_COIN_FLIP_SPIN_MS, so every screen lands together. A flip that reaches
 * this screen after it started spinning (joining mid-spin) shows the winning
 * face at once.
 */
import { useEffect, useState } from 'react';
import { HNST_COIN_CHOICE_SECONDS, HNST_COIN_FLIP_SPIN_MS, HNST_TEAMS, } from '@swtorbit/shared-socket-events';
import { HnstTeamIcon } from './HnstTeamName';
/**
 * How late a flip can reach this screen and still spin in full. Later than
 * this, it is a player joining mid-spin, and the coin shows as landed.
 */
const LATE_ARRIVAL_MS = 1000;
function phaseOnArrival(flip, serverOffsetMs) {
    if (!flip)
        return 'rest';
    const remaining = flip.landsAt - (Date.now() + serverOffsetMs);
    return remaining > HNST_COIN_FLIP_SPIN_MS - LATE_ARRIVAL_MS ? 'spinning' : 'landed';
}
/**
 * Where a flip is on this screen. A flip that arrives fresh spins for
 * HNST_COIN_FLIP_SPIN_MS, then lands; a late one is landed at once. The coin
 * uses it, and so can the text around it, so the result shows as the coin lands.
 */
export function useHnstCoinPhase(flip, serverOffsetMs = 0) {
    // Keyed on the flip: a new snapshot of the same flip keeps its phase.
    const flipKey = flip?.flippedAt ?? null;
    const [state, setState] = useState(() => ({ flipKey, phase: phaseOnArrival(flip, serverOffsetMs) }));
    let { phase } = state;
    if (state.flipKey !== flipKey) {
        phase = phaseOnArrival(flip, serverOffsetMs);
        setState({ flipKey, phase });
    }
    useEffect(() => {
        if (phase !== 'spinning')
            return;
        const timer = setTimeout(() => {
            setState((s) => (s.flipKey === flipKey ? { flipKey, phase: 'landed' } : s));
        }, HNST_COIN_FLIP_SPIN_MS);
        return () => clearTimeout(timer);
    }, [flipKey, phase]);
    return phase;
}
/**
 * A landed coin toss whose winning captain is choosing: their time left, by
 * the server's clock, ticking four times a second. Null when nobody is
 * choosing (not landed yet, already chosen, or the winner hides first).
 */
export function useHnstCoinChoiceTime(toss, serverOffsetMs = 0) {
    const deadline = toss && !toss.choice && toss.flip ? toss.choiceDeadlineAt : null;
    const landsAt = toss?.flip?.landsAt ?? 0;
    const [now, setNow] = useState(() => Date.now() + serverOffsetMs);
    useEffect(() => {
        if (deadline === null)
            return;
        const timer = setInterval(() => setNow(Date.now() + serverOffsetMs), 250);
        return () => clearInterval(timer);
    }, [deadline, serverOffsetMs]);
    if (deadline === null || now < landsAt)
        return null;
    const left = Math.max(0, deadline - now);
    return {
        secondsLeft: Math.ceil(left / 1000),
        percentLeft: Math.min(100, (left / (HNST_COIN_CHOICE_SECONDS * 1000)) * 100),
    };
}
export function HnstCoin({ flip, size = 'base', serverOffsetMs = 0 }) {
    const phase = useHnstCoinPhase(flip, serverOffsetMs);
    const className = [
        'hnst-coin',
        size !== 'base' && `hnst-coin--${size}`,
        flip && `hnst-coin--${flip.winner}`,
        phase === 'spinning' && 'is-spinning',
        phase === 'landed' && 'is-landed',
    ].filter(Boolean).join(' ');
    return (_jsx("div", { className: className, "aria-label": "Coin flip", children: _jsxs("div", { className: "hnst-coin__body", children: [_jsx(Face, { team: "rotworms" }), _jsx(Face, { team: "frogdogs" })] }, flip?.flippedAt ?? 'rest') }));
}
function Face({ team }) {
    return (_jsxs("div", { className: `hnst-coin__face hnst-coin__face--${team}`, children: [_jsx(HnstTeamIcon, { team: team, className: "hnst-coin__icon" }), _jsx("span", { children: HNST_TEAMS[team].name })] }));
}
